import {AfterViewInit, Component, effect, HostListener, input, OnDestroy, ViewChild}                             from '@angular/core';
import {CrudRecordTypes, GlobalDataTypesDict, HkitHeartAlertsIntf, HkitWorkoutIntf, PointWithData}               from '@djsharkk/logr-types';
import {ChartConfiguration, ChartDataset, ChartOptions, Point, ScriptableContext, TimeScaleOptions, TooltipItem} from 'chart.js';
import {DateTime}                                                                                                from 'luxon';
import {takeUntil}                                                                                               from 'rxjs';
import {ChartScatterComponent}                                                                                   from '../../comps/chart-scatter/chart-scatter.component';
import {ChartCatsBase}                                                                                           from '../chart-cats.base';

@Component({
			   selector   : 'app-chart-log-days',
			   imports    : [
				   ChartScatterComponent
			   ],
			   templateUrl: './chart-log-days.component.html',
			   styleUrl   : './chart-log-days.component.css'
		   })
export class ChartLogDaysComponent extends ChartCatsBase<ChartScatterComponent, 'scatter' | 'line'> implements OnDestroy, AfterViewInit {
	@ViewChild(ChartScatterComponent) chartComponent!: ChartScatterComponent;

	dayCount = input<number>()
	dates = input<Date[]>(this.svcDataFilters.dateRange.getValue())

	@HostListener('document:visibilitychange', ['$event'])
	async onVisibilityChange() {
		if (document.visibilityState === 'visible') {
			this.svcChartProps.addNowLine(this.conf.options as ChartOptions);
		}
	}

	borderWidthCalc = (ctx: ScriptableContext<'line'>) => Math.max(1, (ctx.chart.scales['y'].maxHeight / ctx.chart.scales['y'].ticks.length)) - 2

	conf: ChartConfiguration<'scatter' | 'line', Point[]> = {
		data   : {
			datasets: [
				...CrudRecordTypes.map(type => ({
					...this.svcChartProps.crudPointStylesSummary[type],
					pointRadius: (ctx: ScriptableContext<'line'>) => this.borderWidthCalc(ctx) / 2,
					data       : []
				})),
				{
					label      : 'Sleep Weekdays',
					type       : 'line',
					data       : [] as Point[],
					pointRadius: 0,
					borderWidth: ctx => this.borderWidthCalc(ctx),
					borderColor: this.svcChartProps.stripeAnnotationStylesWeekdays.sleep
				},
				{
					label      : 'Sleep Weekends',
					type       : 'line',
					data       : [] as Point[],
					pointRadius: 0,
					borderWidth: ctx => this.borderWidthCalc(ctx),
					borderColor: this.svcChartProps.stripeAnnotationStylesWeekends.sleep
				},
				{
					label           : 'Workouts',
					type            : 'line',
					data            : [] as Point[],
					pointStyle      : 'circle',
					pointBorderWidth: 0,
					pointRadius     : 0,
					borderWidth     : ctx => this.borderWidthCalc(ctx),
					borderColor     : this.svcChartProps.stripeAnnotationStylesWeekdays.workout
				},
				{
					label           : 'Heart Alerts',
					type            : 'line',
					data            : [] as Point[],
					pointStyle      : 'circle',
					pointBorderWidth: 0,
					pointRadius     : 0,
					borderWidth     : 1,
					borderColor     : this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.heart_alert.colorTw, 600)
				},
				...this.svcChartDefaults.rizeDatasetsLine(true)
			],
			labels  : []
		},
		options: this.svcChartProps.optsHour,
		plugins: [],
		type   : 'scatter'
	};


	constructor() {
		super();

		effect(() => {
			this.dates().sort((a, b) => a.getTime() - b.getTime())
		});

		const n = this.dayCount()
		if (n) {
			this.dates().slice(-n)
		}

		const tooltipConfig = {
			...this.conf.options?.plugins?.tooltip
		};

		if (this.conf.options?.plugins?.tooltip) {
			this.conf.options.plugins.tooltip = {
				...tooltipConfig,
				intersect: true,
				mode     : 'nearest',
				callbacks: {
					label: (tooltipItem: TooltipItem<'scatter'>): string | string[] | void => {
						const datapoint = tooltipItem.dataset.data[tooltipItem.dataIndex] as PointWithData;
						const timestampX = DateTime.fromMillis(datapoint.x).toFormat('HH:mm')
						const timestampY = DateTime.fromMillis(datapoint.y).toFormat('ccc dd')
						return `${GlobalDataTypesDict[datapoint.t].name.toUpperCase()}: ${timestampY}, ${timestampX}`
					}

				}
			}
		}

		const defaultScales = {
			...this.conf.options?.scales
		};

		const defaultY: TimeScaleOptions = {
			...defaultScales?.['y'] as TimeScaleOptions
		};
		const defaultX: TimeScaleOptions = {
			...defaultScales?.['x'] as TimeScaleOptions
		};


		if (this.conf.options?.scales && this.conf.options?.scales?.['y']) {

			this.conf.options.scales = {
				...defaultScales,
				x: {
					...defaultX,
					min   : DateTime.fromJSDate(this.dates()[0]).startOf('day').toMillis(),
					max   : DateTime.fromJSDate(this.dates()[0]).endOf('day').minus({days:this.dayCount()}).toMillis(),
					offset: false,
					ticks : {
						...defaultX.ticks,
						mirror  : false,
						callback: (tickValue, i, ticks) => i === 0 ? '' : DateTime.fromMillis(tickValue as number).toFormat('HH').toUpperCase()
					}
				},
				y: {
					...defaultY,
					min     : DateTime.fromJSDate(this.dates()[this.dates().length - 1]).startOf('day').toMillis(),
					max     : DateTime.fromJSDate(this.dates()[0]).startOf('day').minus({days:this.dayCount()}).toMillis(),
					type    : 'time',
					offset  : true,
					position: 'left',
					time    : {
						unit          : 'day',
						displayFormats: {
							day: 'ccc'
						}
					},
					ticks   : {
						...defaultY.ticks,
						mirror  : true,
						align   : 'inner',
						color   : ctx => this.svcUiTheme.getTailwindColor(DateTime.fromMillis(ctx.tick.value).isWeekend ? 'blue' : 'neutral', 600),
						padding : 0,
						z       : 10,
						callback: (tickValue, i, ticks) => DateTime.fromMillis(tickValue as number).toFormat('ccc dd').toUpperCase()
					}
				}
			}
		}
	}

	ngAfterViewInit() {

		this.svcChartProps.addNowLine(this.conf.options as ChartOptions);

		CrudRecordTypes.forEach((type, i) => {
			this.svcDataFactory.crudDataSvcs[type].getRecordsLogDates(this.dates())
												  .pipe(takeUntil(this.dataTrash))
												  .subscribe(records => {
													  this.chartComponent?.updateDataset(
														  i,
														  {
															  data: records
																  .map(record => ({
																	  x: this.svcChartDefaults.equaliseX(record.timestamp.toDate(), this.dates()[0]),
																	  y: this.svcChartDefaults.equaliseY(record.timestamp.toDate())
																  } as Point))
														  }
													  )
												  })
		})


		this.svcDataFactory.svcDataOura.recordsOuraSleep
			.pipe(takeUntil(this.dataTrash))
			.subscribe(records => {

				const data = (weekend = false) =>
					records
						.filter(record => DateTime.fromJSDate(record.day).isWeekend === weekend)
						.map((record) => {
							const gaps: Point = {
								x: NaN,
								y: NaN
							};

							const x1 = record.adjustedBedtimeStart || record.bedtime_start
							const x2 = record.bedtime_end;
							const data: Point[] = [
								gaps,
								{
									x: this.svcChartDefaults.equaliseX(x1, this.dates()[0]),
									y: this.svcChartDefaults.equaliseY(x1)
								},
								{
									x: this.svcChartDefaults.equaliseX(x2, this.dates()[0]),
									y: this.svcChartDefaults.equaliseY(x2)
								},
								gaps
							]

							if (record.nextBedtimeStart && record.nextBedtimeEnd) {
								const x1 = record.nextBedtimeStart;
								const x2 = record.nextBedtimeEnd;
								data.push(
									gaps,
									{
										x: this.svcChartDefaults.equaliseX(x1, this.dates()[0]),
										y: this.svcChartDefaults.equaliseY(x1)
									},
									{
										x: this.svcChartDefaults.equaliseX(x2, this.dates()[0]),
										y: this.svcChartDefaults.equaliseY(x2)
									},
									gaps
								)
							}

							return data
						}).flat()

				this.chartComponent?.updateDataset(
					CrudRecordTypes.length,
					{
						data: data()
					}
				)

				this.chartComponent?.updateDataset(
					CrudRecordTypes.length + 1,
					{
						data: data(true)
					}
				)
			})

		const hkitRecordDataset = (records: (HkitWorkoutIntf | HkitHeartAlertsIntf)[]): Partial<ChartDataset> => ({
			data: records
				.map((record) => {
					const gaps: Point = {
						x: NaN,
						y: NaN
					};

					const data: Point[] = [
						gaps,
						{
							x: this.svcChartDefaults.equaliseX(record.start.toDate(), this.dates()[0]),
							y: this.svcChartDefaults.equaliseY(record.start.toDate())
						},
						{
							x: this.svcChartDefaults.equaliseX(record.end.toDate(), this.dates()[0]),
							y: this.svcChartDefaults.equaliseY(record.end.toDate())
						},
						gaps
					]


					return data
				}).flat()
		})

		this.svcDataFactory.svcDataHkit.recordsWorkouts
			.pipe(takeUntil(this.dataTrash))
			.subscribe(records => this.chartComponent?.updateDataset(CrudRecordTypes.length + 2, hkitRecordDataset(records)))

		this.svcDataFactory.svcDataHkit.recordsHeartAlerts
			.pipe(takeUntil(this.dataTrash))
			.subscribe(records => this.chartComponent?.updateDataset(CrudRecordTypes.length + 3, hkitRecordDataset(records)))

		this.svcDataFactory.svcDataRize.rizeSessions
			.pipe(takeUntil(this.dataTrash))
			.subscribe(records => {
				this.chartComponent?.updateDataset(
					CrudRecordTypes.length + 4,
					{
						data: this.svcChartDefaults.rizeDataPoints('focus', records, this.dates()[0], true)
					}
				)

				this.chartComponent?.updateDataset(
					CrudRecordTypes.length + 5,
					{
						data: this.svcChartDefaults.rizeDataPoints('break', records, this.dates()[0], true)
					}
				)

				this.chartComponent?.updateDataset(
					CrudRecordTypes.length + 6,
					{
						data: this.svcChartDefaults.rizeDataPoints('meeting', records, this.dates()[0], true)
					}
				)
			})


		this.svcDataFactory.svcDataMeds.medsConf
			.pipe(takeUntil(this.dataTrash))
			.subscribe(sched => sched.forEach(s => {

				const x = new Date(this.dates()[0]);

				x.setHours(parseFloat(s.time.split(':')[0]), parseFloat(s.time.split(':')[1]), 0, 0)

				this.svcChartProps.buildAnnotation({
													   type       : 'line',
													   xMin       : x.getTime(),
													   xMax       : x.getTime(),
													   borderWidth: 1,
													   borderColor: this.svcUiTheme.getTailwindColor('purple', 800, 0.5),
													   borderDash : [
														   2,
														   2
													   ]
												   }, s.label, this.conf.options as ChartOptions)
			}))

	}


}
